import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getPurchases } from "../../services/userData";
import { Button, Spinner, Badge } from "../../components/ui/UI";
import CourseThumbnail from "../../components/ui/CourseThumbnail";
import PDFViewer from "../../components/ui/PDFViewer";
import styles from "./MyCourses.module.css";

export default function MyCoursesPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [openCourse, setOpenCourse] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (!user) {
      navigate("/login");
      return;
    }
    loadPurchases();
    // eslint-disable-next-line
  }, [user]);

  const loadPurchases = async () => {
    setLoading(true);
    try {
      const data = await getPurchases({
        phone: user.phone,
        email: user.email,
      });
      setPurchases(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className={styles.page}>
      <div className="container">
        {/* Header */}
        <div className={styles.header}>
          <div>
            <p className={styles.eyebrow}>My Library</p>
            <h1 className={styles.title}>
              Welcome back{user.name ? `, ${user.name}` : ""}
            </h1>
            <p className={styles.sub}>
              {purchases.length === 0
                ? "You haven't bought any courses yet."
                : `${purchases.length} course${purchases.length === 1 ? "" : "s"} in your library`}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }}>
            Logout
          </Button>
        </div>

        {/* Loading */}
        {loading && (
          <div className={styles.loadingBox}>
            <Spinner />
            <p>Loading your courses…</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && purchases.length === 0 && (
          <div className={styles.empty}>
            <span>📚</span>
            <h3>No courses yet</h3>
            <p>Browse our catalog and pick up a course to get started.</p>
            <Button variant="primary" onClick={() => navigate("/category/all")}>
              Browse Courses →
            </Button>
          </div>
        )}

        {/* Course grid */}
        {!loading && purchases.length > 0 && (
          <div className={styles.grid}>
            {purchases.map((p, i) => (
              <div key={i} className={styles.courseCard}>
                <CourseThumbnail thumbnail={p.thumbnail} title={p.courseTitle} size="card" />
                <div className={styles.cardBody}>
                  <Badge color="green">✓ Owned</Badge>
                  <h3 className={styles.courseTitle}>{p.courseTitle}</h3>
                  <p className={styles.courseMeta}>
                    Purchased {new Date(p.purchasedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                  <div className={styles.courseActions}>
                    <Button variant="primary" size="sm" fullWidth onClick={() => setOpenCourse(p)}>
                      📖 Open Course
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      {openCourse && (
        <PDFViewer
          course={openCourse}
          user={user}
          onClose={() => setOpenCourse(null)}
        />
      )}
    </div>
  );
}
